/**
 * Shared Add Menu Actions Hook
 *
 * Extracts the common add-menu logic duplicated between HeaderCreateMenu
 * and the LeftSidebar AddMenuPopover: expandable action toggling, recent
 * player formatting, player selection for reports/notes/watchlists, and
 * watchlist actionsheet state management.
 * @module hooks/useAddMenuActions
 */

import { Href, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";

import { PlayerSearchPlayerFragment } from "@/generated/graphql";
import useCreateWatchlistActions from "@/hooks/useCreateWatchlistActions";
import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";
import useOpenCreateNoteForPlayer from "@/hooks/useOpenCreateNoteForPlayer";
import { useRecentPlayers } from "@/hooks/useRecentPlayers";
import { RecentPlayer } from "@/stores/recentPlayers";

/** Expanded menu action type */
type ExpandedAction = "report" | "note" | "watchlist" | null;

/** Formatted recent player item for display in menus */
interface RecentPlayerItem {
  readonly id: string;
  readonly name: string;
}

/** Options for useAddMenuActions */
interface UseAddMenuActionsOptions {
  /** Callback invoked before navigation to dismiss the popover/panel */
  readonly onDismiss: () => void;
}

/**
 * Converts a RecentPlayer to a minimal PlayerSearchPlayerFragment
 * for the watchlist actionsheet.
 * @param player - The recent player to convert
 * @returns A PlayerSearchPlayerFragment with required fields
 */
const createPlayerFragment = (
  player: RecentPlayer
): PlayerSearchPlayerFragment => ({
  __typename: "Player",
  id: player.id,
  name: player.name,
  firstName: player.firstName,
  lastName: player.lastName,
  // RecentPlayer carries no knownName; null keeps buildPlayerDisplayName from
  // duplicating the name into "First First Last Last" (SE-4725).
  knownName: null,
  isManual: false,
  isWatchedByCurrentUser: false,
  positions: null,
  team: null,
  bioData: null,
});

/**
 * Shared hook for add menu actions used by HeaderCreateMenu and AddMenuPopover.
 * Manages expandable action state, recent player formatting, player selection
 * navigation, and watchlist actionsheet lifecycle.
 * @param options - Configuration including onDismiss callback
 * @param options.onDismiss - Callback invoked before navigation to dismiss the popover/panel
 * @returns State and handlers for the add menu UI
 */
const useAddMenuActions = ({ onDismiss }: UseAddMenuActionsOptions) => {
  const router = useRouter();
  const { recentPlayers } = useRecentPlayers();
  const { handleAddWatchlistPress, loading: watchlistLoading } =
    useCreateWatchlistActions({});
  const getPlayerUrl = useGetExpandedPlayerUrl();
  const openCreateNoteForPlayer = useOpenCreateNoteForPlayer();

  const [expandedAction, setExpandedAction] = useState<ExpandedAction>(null);
  const [showWatchlistActionsheet, setShowWatchlistActionsheet] =
    useState(false);
  const [selectedPlayerForWatchlist, setSelectedPlayerForWatchlist] =
    useState<PlayerSearchPlayerFragment | null>(null);

  const handleWriteReport = useCallback(() => {
    setExpandedAction(prev => (prev === "report" ? null : "report"));
  }, []);

  const handleAddNote = useCallback(() => {
    setExpandedAction(prev => (prev === "note" ? null : "note"));
  }, []);

  const handleAddToWatchlist = useCallback(() => {
    setExpandedAction(prev => (prev === "watchlist" ? null : "watchlist"));
  }, []);

  const resetExpandedAction = useCallback(() => {
    setExpandedAction(null);
  }, []);

  const handleSelectPlayerForReport = useCallback(
    (playerId: string) => {
      onDismiss();
      setExpandedAction(null);
      router.push(getPlayerUrl(`/players/${playerId}/matches`) as Href);
    },
    [getPlayerUrl, router, onDismiss]
  );

  const handleSelectPlayerForNote = useCallback(
    (playerId: string) => {
      onDismiss();
      setExpandedAction(null);
      // Deep link lives in useOpenCreateNoteForPlayer so this menu and the Home
      // quick action (SE-6845) cannot drift from MainContainer's reader.
      openCreateNoteForPlayer(playerId);
    },
    [openCreateNoteForPlayer, onDismiss]
  );

  const handleSelectPlayerForWatchlist = useCallback(
    (playerId: string) => {
      const player = recentPlayers.find(p => p.id === playerId);
      if (player) {
        onDismiss();
        setExpandedAction(null);
        setSelectedPlayerForWatchlist(createPlayerFragment(player));
        setShowWatchlistActionsheet(true);
      }
    },
    [recentPlayers, onDismiss]
  );

  const handleCloseWatchlistActionsheet = useCallback(() => {
    setShowWatchlistActionsheet(false);
    setSelectedPlayerForWatchlist(null);
  }, []);

  const recentPlayerItems: readonly RecentPlayerItem[] = useMemo(
    () =>
      recentPlayers.map(player => ({
        id: player.id,
        name: player?.name ?? "",
      })),
    [recentPlayers]
  );

  return {
    expandedAction,
    recentPlayerItems,
    handleWriteReport,
    handleAddNote,
    handleAddToWatchlist,
    resetExpandedAction,
    handleSelectPlayerForReport,
    handleSelectPlayerForNote,
    handleSelectPlayerForWatchlist,
    showWatchlistActionsheet,
    selectedPlayerForWatchlist,
    handleCloseWatchlistActionsheet,
    handleAddWatchlistPress,
    watchlistLoading,
  } as const;
};

export default useAddMenuActions;
export type { ExpandedAction, RecentPlayerItem };
